// client/src/routes/app/expenses/new.tsx — SCR-EXP-002 (create) · EXP-005 (04 §8.7).
//
// `Expenses.Write`, not `Expenses.Read`: an accountant sees the ledger and cannot add a cost
// (03 §17.1), and the client mirror has to say so before the API's 403 does — the ledger hides
// the button already, and this renders SCR-SYS-403 for the URL that was typed anyway (§9.2.2).
//
// Saving lands back on the ledger rather than on a detail screen, because there is no expense
// detail screen: 04 §8.7 gives the module a LIST and a form, and the row the landlord just
// created is on the list they came from.
//
// THE RECEIPT IS FILED SECOND, and it has to be: a bon is linked to the cost's own id and the
// cost has no id until `POST /api/v1/expenses` answers (D-2212). `useAttachReceipt` never
// rejects — the save has already happened by then — so a refused upload becomes a sentence
// naming the edit screen, and the landlord still lands on the row they just created.
//
// ONE PAGE FOR BOTH KINDS OF COST (D-2920). The page asks first whether the cost happens once or
// comes back, and swaps the form under the question: „O singură dată" is `ExpenseForm`, „Se
// repetă" is SCR-EXP-003's rule builder in its page presentation. The answer lives in the URL
// (`?repeat=true`) so a reload keeps it, and it replaces the history entry rather than adding
// one — Back leaves the page, it does not flip the switch. What the two forms share is carried
// across a switch through `frequency.ts`; each form's own fields are kept for a round trip.
//
// A saved rule has already written the expenses it owes up to this month (D-2921), so the page
// lands on the register filtered to „Recurente", where they are. A rule that owes nothing yet
// lands on the rules list instead, where it is — an empty filtered register would read as a
// save that did nothing.

import { useCallback, useRef } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ChoicePill } from '@/components/forms/ChoicePill';
import { FormSection } from '@/components/forms/FormSection';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequirePermission } from '@/components/layout/route-guards';
import { ExpenseForm } from '@/features/expenses/ExpenseForm';
import {
  expenseFromRule,
  ruleFromExpense,
  type ExpenseFrequency,
} from '@/features/expenses/frequency';
import { useCreateExpense, useCreateRecurringExpense } from '@/features/expenses/queries';
import { useAttachReceipt, type ReceiptDraft } from '@/features/expenses/receipt';
import { RecurringRuleForm } from '@/features/expenses/RecurringRuleForm';
import {
  emptyRecurringRuleForm,
  toRecurringInput,
  type RecurringRuleFormValues,
} from '@/features/expenses/recurring-schema';
import {
  emptyExpenseForm,
  toExpenseInput,
  type ExpenseFormValues,
} from '@/features/expenses/schema';
import { urlBoolean, zUrlBoolean } from '@/lib/url-state';
import { wireNumber } from '@/lib/wire';
import { toast } from '@/stores/toast-store';

/**
 * `repeat` admits both spellings `lib/url-state.ts` describes: the switch writes the string, and
 * a link somebody typed or shared may carry the boolean the router parses it into.
 */
const newExpenseSearchSchema = z.object({
  repeat: zUrlBoolean.optional(),
});

export const Route = createFileRoute('/app/expenses/new')({
  validateSearch: newExpenseSearchSchema,
  component: NewExpenseScreen,
});

/** Today as the `DateOnly` wire format, in local time — „which day is the landlord filing this". */
function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${String(now.getFullYear())}-${month}-${day}`;
}

function NewExpenseScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const create = useCreateExpense();
  const createRule = useCreateRecurringExpense();
  const attachReceipt = useAttachReceipt();
  const recurring = urlBoolean(search.repeat) === true;

  // The last values of each form, kept across a switch. Refs rather than state: the copy is read
  // only at the moment of a switch, and a render of the page per keystroke would buy nothing.
  const today = todayIso();
  const onceDraft = useRef<ExpenseFormValues>(emptyExpenseForm(today));
  const ruleDraft = useRef<RecurringRuleFormValues>(emptyRecurringRuleForm(today));
  const keepOnce = useCallback((values: ExpenseFormValues) => {
    onceDraft.current = values;
  }, []);
  const keepRule = useCallback((values: RecurringRuleFormValues) => {
    ruleDraft.current = values;
  }, []);

  const choose = (next: ExpenseFrequency) => {
    if ((next === 'recurring') === recurring) return;
    if (next === 'recurring') {
      ruleDraft.current = ruleFromExpense(onceDraft.current, ruleDraft.current);
    } else {
      onceDraft.current = expenseFromRule(ruleDraft.current, onceDraft.current);
    }
    void navigate({
      to: '/app/expenses/new',
      // A boolean, not the string: the router writes a string JSON-quoted (`?repeat=%22true%22`)
      // and a boolean bare, and this URL is one a landlord may read or share.
      search: next === 'recurring' ? { repeat: true } : {},
      replace: true,
    });
  };

  const leave = () => {
    void navigate({ to: '/app/expenses' });
  };

  return (
    <RequirePermission permission="Expenses.Write">
      <PageHeader
        title={t('expenses.form.createTitle')}
        description={
          recurring ? t('expenses.form.createLeadRecurring') : t('expenses.form.createLead')
        }
        backTo="/app/expenses"
        backLabel={t('expenses.list.title')}
      />

      <div className="space-y-5">
        <FormSection
          title={t('expenses.frequency.title')}
          description={
            recurring ? t('expenses.frequency.recurringLead') : t('expenses.frequency.onceLead')
          }
          icon="calendar"
        >
          <div
            role="radiogroup"
            aria-label={t('expenses.frequency.title')}
            className="grid gap-2 sm:grid-cols-2"
          >
            <ChoicePill
              name="expense-frequency"
              value="once"
              icon="receipt"
              label={t('expenses.frequency.once')}
              description={t('expenses.frequency.onceHint')}
              checked={!recurring}
              onChange={(checked) => {
                if (checked) choose('once');
              }}
            />
            <ChoicePill
              name="expense-frequency"
              value="recurring"
              icon="arrow-path"
              label={t('expenses.frequency.recurring')}
              description={t('expenses.frequency.recurringHint')}
              checked={recurring}
              onChange={(checked) => {
                if (checked) choose('recurring');
              }}
            />
          </div>
        </FormSection>

        {recurring ? (
          <RecurringRuleForm
            key="recurring"
            presentation="page"
            ruleId={null}
            initialValues={ruleDraft.current}
            onValuesChange={keepRule}
            submitLabel={t('expenses.recurring.builder.create')}
            onSubmit={async (values: RecurringRuleFormValues) => {
              const created = await createRule.mutateAsync({ body: toRecurringInput(values) });
              const generated = wireNumber(created.generatedCount);
              if (generated > 0) {
                toast.success(t('expenses.frequency.savedWithRows', { count: generated }));
                await navigate({ to: '/app/expenses', search: { isRecurring: true } });
              } else {
                toast.success(t('expenses.frequency.savedNothingYet'));
                await navigate({ to: '/app/expenses/recurring' });
              }
            }}
            onCancel={leave}
          />
        ) : (
          <ExpenseForm
            key="once"
            initialValues={onceDraft.current}
            onValuesChange={keepOnce}
            submitLabel={t('expenses.form.create')}
            onSubmit={async (values: ExpenseFormValues, receipt: ReceiptDraft | null) => {
              const created = await create.mutateAsync({ body: toExpenseInput(values) });
              toast.success(t('expenses.form.created'));
              await attachReceipt(created.id, receipt);
              await navigate({ to: '/app/expenses' });
            }}
            onCancel={leave}
          />
        )}
      </div>
    </RequirePermission>
  );
}
